import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "../Card.js";
import { Failed, Loading } from "../ViewState.js";
import { cn } from "../../lib/cn.js";
import type { Async } from "../../lib/useAsync.js";
import { formatKwh } from "../../lib/energy.js";
import { SERIES, segments, type ChartBucket, type ChartData } from "../../lib/chart.js";

export const RANGES = ["7 days", "6 months", "1 year"] as const;
export type ChartRange = (typeof RANGES)[number];

interface Props {
  state: Async<ChartData>;
  onRetry: () => void;
  range: ChartRange;
  onRangeChange: (range: ChartRange) => void;
  onExpand?: () => void;
  className?: string;
}

/**
 * Energy over time, one line per flow: solar, grid, battery charge and discharge, and
 * the house.
 *
 * The design drew one series of bars. Slice 5c draws the flows side by side
 * because the operator asked on 2026-09-30 for the week to show grid, battery
 * and solar together, perhaps as lines (`REQUIREMENTS.md` section 1 job 4). A
 * line per flow reads as a comparison where five bars a bucket would not fit a
 * year at 360px. The key states each flow's total for the range, so the
 * week's, month's and year's figures for every flow are on screen, as the
 * server computed them.
 *
 * What slice 5 established still holds: a partly recorded bucket is drawn as
 * one -- its points hollow -- and a bucket with no readings breaks every line
 * rather than drawing zeros through it. The y scale comes from the data, never
 * the design's fixed 25,000 ceiling.
 */
export function MainChartWidget({
  state,
  onRetry,
  range,
  onRangeChange,
  onExpand,
  className,
}: Props) {
  const [hovered, setHovered] = useState<ChartBucket | null>(null);

  const buckets = state.kind === "ready" ? state.value.buckets : [];
  const recorded = buckets.some((bucket) => bucket.coverage !== "none");
  const ceiling = Math.max(
    0,
    ...buckets.flatMap((bucket) => Object.values(bucket.values).map((value) => value ?? 0)),
  );
  const scale = ceiling === 0 ? 1 : ceiling;
  const ticks = [1, 0.8, 0.6, 0.4, 0.2, 0].map((fraction) => scale * fraction);
  // Whole kilowatt-hours read as six zeros on a day with a small load.
  const digits = scale >= 10 ? 0 : scale >= 1 ? 1 : 2;

  return (
    <Card
      onExpand={onExpand}
      title="Energy history"
      headerRight={<RangeSelect range={range} onChange={onRangeChange} />}
      className={cn(
        "col-span-1 flex flex-col justify-between md:col-span-2 lg:row-span-2",
        className,
      )}
    >
      <div className="relative my-2 h-56 w-full select-none sm:h-64">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-6 top-0 flex flex-col justify-between"
        >
          {ticks.map((tick, index) => (
            <div key={index} className="flex w-full items-center">
              <span className="tabular w-11 pr-2 text-right text-[10px] text-ink-muted sm:text-[11px]">
                {recorded ? tick.toLocaleString("en-GB", { maximumFractionDigits: digits }) : ""}
              </span>
              <div
                className={cn(
                  "h-px flex-1",
                  index === ticks.length - 1 ? "bg-line-strong" : "bg-line",
                )}
              />
            </div>
          ))}
        </div>

        {state.kind !== "ready" || !recorded ? (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <ChartNotice state={state} onRetry={onRetry} />
          </div>
        ) : (
          // The grid's first and last lines sit 8px in from each end of its
          // box, so the plot shares that span: zero on the bottom line, the
          // ceiling on the top one.
          <Plot buckets={buckets} scale={scale} onHover={setHovered} />
        )}

        {hovered !== null && (
          <p className="tabular pointer-events-none absolute left-16 top-2 z-20 max-w-[80%] rounded-lg border border-line bg-card px-3 py-1.5 text-xs text-ink shadow-xl">
            {hovered.label}
          </p>
        )}

        <Labels buckets={buckets} />
      </div>

      <ChartFooter
        range={range}
        data={state.kind === "ready" ? state.value : null}
        partial={buckets.some((bucket) => bucket.coverage === "partial")}
      />
    </Card>
  );
}

/** The design's range picker, in the card's header. */
function RangeSelect({
  range,
  onChange,
}: {
  range: ChartRange;
  onChange: (range: ChartRange) => void;
}) {
  const selectId = useId();
  return (
    <span className="relative inline-block">
      <label htmlFor={selectId} className="sr-only">
        Time range
      </label>
      <select
        id={selectId}
        value={range}
        onChange={(event) => {
          onChange(event.target.value as ChartRange);
        }}
        className="cursor-pointer appearance-none rounded-lg border border-line bg-line/40 py-1.5 pl-3 pr-7 text-xs font-medium text-ink outline-none transition-colors hover:border-line-strong"
      >
        {RANGES.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
      <ChevronDown
        size={12}
        aria-hidden="true"
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted"
      />
    </span>
  );
}

/** Where a value sits, as a percentage down from the top of the plot. */
function heightOf(value: number, scale: number): number {
  return 100 - (value / scale) * 100;
}

/**
 * The lines, drawn in a 100 by 100 box stretched to the plot, and one column
 * per bucket above them carrying its points, its words and its hover.
 */
function Plot({
  buckets,
  scale,
  onHover,
}: {
  buckets: readonly ChartBucket[];
  scale: number;
  onHover: (bucket: ChartBucket | null) => void;
}) {
  return (
    <div className="absolute bottom-8 left-11 right-0 top-2 px-1">
      <Lines buckets={buckets} scale={scale} />
      <div className="absolute inset-0 flex px-1">
        {buckets.map((bucket, index) => (
          <span
            key={index}
            onMouseEnter={() => {
              onHover(bucket);
            }}
            onMouseLeave={() => {
              onHover(null);
            }}
            className="relative h-full flex-1"
          >
            <span className="sr-only">{bucket.label}</span>
            {SERIES.map(({ key, fill, ring }) => {
              const value = bucket.values[key];
              if (value === null) return null;
              return (
                <span
                  key={key}
                  aria-hidden="true"
                  data-series={key}
                  className={cn(
                    "absolute left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
                    ring,
                    bucket.coverage === "partial" ? "bg-card" : fill,
                  )}
                  style={{ top: `${String(heightOf(value, scale))}%` }}
                />
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
}

/** One polyline per unbroken run of each flow. */
function Lines({ buckets, scale }: { buckets: readonly ChartBucket[]; scale: number }) {
  const x = (index: number) => ((index + 0.5) / buckets.length) * 100;
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="absolute inset-y-0 left-1 right-1 h-full w-[calc(100%-0.5rem)] overflow-visible"
    >
      {SERIES.map((series) =>
        segments(buckets.map((bucket) => bucket.values[series.key])).map((segment) => (
          <polyline
            key={`${series.key}-${String(segment[0]?.index)}`}
            points={segment
              .map(({ index, value }) => `${String(x(index))},${String(heightOf(value, scale))}`)
              .join(" ")}
            fill="none"
            strokeWidth="2"
            strokeDasharray={series.dash}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            className={series.stroke}
          />
        )),
      )}
    </svg>
  );
}

/** The x labels, each centred under the buckets it names. */
function Labels({ buckets }: { buckets: readonly ChartBucket[] }) {
  const groups = [...new Set(buckets.map((bucket) => bucket.group))].map((group) => {
    const members = buckets.filter((bucket) => bucket.group === group);
    return { group, narrow: members[0]?.narrow ?? group, span: members.length };
  });
  return (
    <div className="absolute bottom-0 left-11 right-0 flex px-1 text-[10px] font-medium text-ink-muted sm:text-[11px]">
      {groups.map(({ group, narrow, span }) => (
        <span
          key={group}
          className="min-w-0 truncate text-center"
          style={{ flexGrow: span, flexBasis: 0 }}
        >
          <span className="sm:hidden">{narrow}</span>
          <span className="hidden sm:inline">{group}</span>
        </span>
      ))}
    </div>
  );
}

/** The range, how much of it was recorded, and a key giving each flow's total. */
function ChartFooter({
  range,
  data,
  partial,
}: {
  range: ChartRange;
  data: ChartData | null;
  partial: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-4 text-xs sm:text-sm">
      <p className="text-ink-muted">
        Showing <span className="font-semibold text-ink">{range}</span>
        {data !== null && data.note !== null && <span>{` · ${data.note}`}</span>}
      </p>
      <ul
        aria-label="Chart key"
        className="grid grid-cols-1 gap-x-6 gap-y-1 font-medium text-ink-muted sm:grid-cols-2"
      >
        {SERIES.map((series) => (
          <KeyEntry
            key={series.key}
            series={series}
            total={data === null ? undefined : data.totals[series.key]}
          />
        ))}
        {partial && (
          <li className="flex items-center gap-2">
            <span aria-hidden="true" className="h-2 w-2 rounded-full border-2 border-ink-muted" />
            <span>Partly recorded</span>
          </li>
        )}
      </ul>
    </div>
  );
}

/** One flow in the key: its line as drawn, its name, and its total once loaded. */
function KeyEntry({
  series,
  total,
}: {
  series: (typeof SERIES)[number];
  total: number | null | undefined;
}) {
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2">
        <svg aria-hidden="true" viewBox="0 0 20 4" className="h-1 w-5 overflow-visible">
          <line
            x1="0"
            y1="2"
            x2="20"
            y2="2"
            strokeWidth="2"
            strokeDasharray={series.dash}
            strokeLinecap="round"
            className={series.stroke}
          />
        </svg>
        <span>{series.label}</span>
      </span>
      {total !== undefined && <span className="tabular text-ink">{formatKwh(total)}</span>}
    </li>
  );
}

/** What fills the plot when there is nothing to draw, and why. */
function ChartNotice({ state, onRetry }: { state: Async<ChartData>; onRetry: () => void }) {
  if (state.kind === "loading") return <Loading label="Loading energy history…" />;
  if (state.kind === "error") return <Failed message={state.message} onRetry={onRetry} />;
  return (
    <p className="text-sm text-ink-muted">
      No readings were recorded in this range. Pick a longer range, or check the Telemetry page if
      the inverter should have been reporting.
    </p>
  );
}
